<template>
  <div class="apex-tree-chart" id="apex-tree-container"></div>
</template>

<script>
export default {
  name: "ApexTreeChart",
  props: {
    data: {
      type: Object,
      default: () => ({
        id: "1",
        data: {
          name: "Data Hub",
          icon: "bi-house",
        },
        children: [],
      }),
    },
    entityType: {
      type: String,
      default: null,
    },
  },
  methods: {
    create_chart() {
      // Normalize data to avoid undefined content in nodeTemplate
      const ensureNodeShape = (node) => {
        if (!node) return null;
        node.data = node.data || {};
        node.data.name = node.data.name || node.name || "";
        node.data.id = node.id;
        // Default icons by heuristics if not provided by backend
        if (!node.data.icon) {
          const label = (node.data.name || "").toLowerCase();
          if (label.includes("workflow")) node.data.icon = "bi-diagram-3";
          else if (label.includes("application")) node.data.icon = "bi-file-earmark-text";
          else if (label.includes("service")) node.data.icon = "bi-gear";
          else if (this.entityType === "enterprise")
            node.data.icon = "bi-building";
          else node.data.icon = "bi-person";
        }
        // Set type based on icon
        if (node.data.icon === "bi-file-earmark-text") {
          node.data.type = "application";
        } else if (node.data.icon === "bi-diagram-3") {
          node.data.type = "workflow";
        } else if (node.data.icon === "bi-gear") {
          node.data.type = "service";
        } else {
          node.data.type = "entity";
        }
        node.options = node.options || {};
        if (Array.isArray(node.children))
          node.children = node.children.map(ensureNodeShape);
        return node;
      };

      const data = ensureNodeShape(JSON.parse(JSON.stringify(this.data)));
      const options = {
        contentKey: "data",
        width: this.$el.offsetWidth - 100 || 800,
        height: 700,
        nodeWidth: 260,
        nodeHeight: 86,
        fontColor: "#333",
        borderColor: "transparent",
        childrenSpacing: 50,
        siblingSpacing: 20,
        direction: "left",
        enableExpandCollapse: true,
        nodeTemplate: (content) => {
          const icon = content && content.icon ? content.icon : "bi-circle";
          const name = content && content.name ? content.name : "";
          const id = content && content.id ? content.id : "";
          const type = content && content.type ? content.type : "";
          
          // Determine node type and badge based on icon
          let badge = "";
          const primaryColor = "var(--primary-color, #007bff)"; // Default primary color fallback
          
          if (icon === "bi-file-earmark-text") {
            badge = "Application";
          } else if (icon === "bi-diagram-3") {
            badge = "Workflow";
          } else if (icon === "bi-gear") {
            badge = "Service";
          }
          // Entity nodes (root level) don't get badges
          
          // Professional card UI with smoke white background and primary color icons
          return `
            <div class="tree-node" data-type="${type}" data-id="${id}" style="display:flex; align-items:center; height:100%; border-radius:8px; background:#fff; padding:12px 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); border: 1px solid #bbb; cursor:pointer;">
              <div style="flex:0 0 auto; width:36px; height:36px; border-radius:6px; background: #ffffff; display:flex; align-items:center; justify-content:center; margin-right:12px; box-shadow: 0 1px 4px rgba(0,0,0,0.1); border: 1px solid #ddd;">
                <i class="${icon}" style="font-size:18px; line-height:1; color:${primaryColor};"></i>
              </div>
              <div style="flex:1 1 auto; min-width:0; display:flex; flex-direction:column; gap:4px;">
                <div style="font-weight:600; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size:14px; color:#333; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${name}</div>
                ${
                  badge
                    ? `<div style="display:inline-block; font-size:10px; font-weight:500; padding:2px 8px; border-radius:12px; background: ${primaryColor}; color:#ffffff; width:max-content; letter-spacing:0.025em;">${badge}</div>`
                    : ""
                }
              </div>
            </div>
          `;
        },
        canvasStyle: "border: 1px solid #e0e0e0; background: #fafafa;",
        enableToolbar: true,
      };
      const tree = new window.ApexTree(
        document.getElementById("apex-tree-container"),
        options
      );
      tree.render(data);
      // Add click event listener
      this.$nextTick(() => {
        const container = document.getElementById("apex-tree-container");
        container.addEventListener('click', (e) => {
          const target = e.target.closest('.tree-node');
          if (target) {
            const type = target.dataset.type;
            const id = target.dataset.id;
            this.$emit('node-click', { type, id });
          }
        });
      });
    },
  },
  async mounted() {
    const load_script = (src) =>
      new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve();
        } else {
          const s = document.createElement("script");
          s.src = src;
          s.async = false;
          s.onload = resolve;
          s.onerror = reject;
          document.head.appendChild(s);
        }
      });

    await load_script("https://cdn.jsdelivr.net/npm/apextree");
    this.create_chart();
  },
  watch: {
    data: {
      handler() {
        this.$nextTick(() => {
          this.create_chart();
        });
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.apex-tree-chart {
  min-height: 600px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px;
  background: #fafafa;
}
</style>
